/**
 * Environment Configuration
 * Handles environment variables for secure configuration management
 */

// Environment variable utility functions
const getEnvVar = (key: string, defaultValue?: string): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key] || defaultValue || '';
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue || '';
  }
  return defaultValue || '';
};

const getEnvBoolean = (key: string, defaultValue: boolean = false): boolean => {
  const value = getEnvVar(key);
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

const getEnvNumber = (key: string, defaultValue: number = 0): number => {
  const value = getEnvVar(key);
  if (!value) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Environment Configuration
export const ENV_CONFIG = {
  // OAuth Configuration - from environment variables
  OAUTH: {
    CLIENT_ID: getEnvVar('VITE_OAUTH_CLIENT_ID', 'WNvw26HKqN7dJx9ATSxUhQxJPMLp1GsM'),
    CLIENT_SECRET: getEnvVar('VITE_OAUTH_CLIENT_SECRET', 'Zj5r7R-Qpn_6DQ0D-XOCG_6oCm_sV5rAqKaHPE6y-sxKSZf2lVQVIQUKHdcLW5wG'),
    GRANT_TYPE: getEnvVar('VITE_OAUTH_GRANT_TYPE', 'client_credentials'),
  },

  // API Configuration
  API: {
    BASE_URL: getEnvVar('VITE_API_BASE_URL', 'https://dev.aws1.apis.libertyglobal.com'),
    TIMEOUT: getEnvNumber('VITE_API_TIMEOUT', 30000),
    RETRY_ATTEMPTS: getEnvNumber('VITE_API_RETRY_ATTEMPTS', 3),
  },

  // OneAI Headers
  ONEAI: {
    USER_ID: getEnvVar('VITE_ONEAI_USER_ID', 'CUST-IE-102'),
    USERNAME: getEnvVar('VITE_ONEAI_USERNAME', 'Jamie'),
    FORMAT_ID: getEnvVar('VITE_ONEAI_FORMAT_ID', 'ONEASSIST_SUMMARY_GENUI_PROMPT'),
  },

  // Development Settings
  DEVELOPMENT: {
    ENABLE_MOCK_MODE: getEnvBoolean('VITE_ENABLE_MOCK_MODE', false),
    USE_PROXY: getEnvBoolean('VITE_USE_PROXY', true),
    FORCE_PROXY_MODE: getEnvBoolean('VITE_FORCE_PROXY_MODE', true),
  },

  // Widget Configuration
  WIDGET: {
    Z_INDEX: getEnvNumber('VITE_WIDGET_Z_INDEX', 9999),
    ENABLE_ANIMATIONS: getEnvBoolean('VITE_WIDGET_ENABLE_ANIMATIONS', true),
    THEME: getEnvVar('VITE_WIDGET_THEME', 'light'),
  },

  // Environment Detection
  IS_PRODUCTION: getEnvVar('NODE_ENV') === 'production',
  IS_DEVELOPMENT: getEnvVar('NODE_ENV') !== 'production',
} as const;

// Export utility functions for use in other files
export { getEnvVar, getEnvBoolean, getEnvNumber };

// Validation function to check if required environment variables are set
export const validateEnvironmentConfig = (): { isValid: boolean; missing: string[] } => {
  const requiredVars = [
    'VITE_OAUTH_CLIENT_ID',
    'VITE_OAUTH_CLIENT_SECRET',
  ];

  const missing: string[] = [];
  
  for (const varName of requiredVars) {
    if (!getEnvVar(varName)) {
      missing.push(varName);
    }
  }

  return {
    isValid: missing.length === 0,
    missing,
  };
};

// Log configuration status (only in development)
if (ENV_CONFIG.IS_DEVELOPMENT) {
  const validation = validateEnvironmentConfig();
  if (!validation.isValid) {
    console.warn('⚠️ Missing required environment variables:', validation.missing);
    console.warn('📝 Please check your .env.local file or environment configuration');
  } else {
    console.log('✅ Environment configuration loaded successfully');
  }
}
